import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Check, Search, X } from 'lucide-react';

/**
 * Single grouped, searchable complaint-type dropdown - each parent complaint
 * type is a row in the left-hand list; one with `sub_options` opens a
 * flyout panel to its right (on hover or click) listing those sub-options,
 * rather than expanding inline. A parent with no sub-options is itself
 * selectable instead (there's nothing finer to pick, so nothing to flyout).
 * Picking any leaf closes the panel immediately; the trigger then shows the
 * parent label on its own line with the sub-type label below it, for a leaf
 * parent it's just the one line.
 *
 * Typography matches ItemPickerDropdown (the sibling custom dropdown in
 * this flow) rather than ad hoc sizes - trigger text 14px/20px regular,
 * row labels 14px/18px, secondary/muted text 11px/14px, section headers
 * 12px/16px medium.
 *
 * The trigger also carries a clear ("x") button once something's selected -
 * clearing re-opens the panel immediately rather than just resetting the
 * selection, since clearing is almost always followed by picking again.
 *
 * Portal-rendered and positioned off the trigger's bounding rect, same
 * pattern as ItemPickerDropdown - the accordion card clips overflow to keep
 * its rounded corners, which would otherwise cut the panel off. The
 * sub-option flyout is positioned off the hovered/active row's own rect.
 */
const ComplaintTypeDropdown = ({
  options = [],
  selectedParentSlug = null,
  selectedSubSlug = null,
  onSelect = () => {},
  placeholder = 'Select a complaint type',
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [query, setQuery] = useState('');
  const [activeParentSlug, setActiveParentSlug] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const flyoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const rowRefs = useRef({});

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const top = rect.bottom + 4;
      setPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(160, Math.min(360, window.innerHeight - top - 16)),
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target) ||
        flyoutRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Re-opening starts with the already-picked parent's flyout showing (so
  // the current pick stays visible without the agent re-hovering it by
  // hand).
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveParentSlug(selectedParentSlug || null);
      setFlyoutPosition(null);
      searchInputRef.current?.focus();
    }
  }, [open, selectedParentSlug]);

  const selectedParent = options.find(
    (option) => option.value === selectedParentSlug,
  );
  const selectedSub = selectedParent?.subOptions?.find(
    (sub) => sub.value === selectedSubSlug,
  );
  const hasSelection = Boolean(selectedParent);

  const handlePick = (parent, sub) => {
    onSelect(parent.value, sub ? sub.value : null);
    setOpen(false);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onSelect(null, null);
    setOpen(true);
  };

  // Flyout mirrors the main panel's width and anchors to whichever side of
  // it has room - the main panel can itself sit near either edge of the
  // viewport (the trigger might be anywhere on the page), so a flyout
  // hard-coded to "always open rightward" clips off-screen whenever the
  // panel is near the right edge. Falls back to clamping within the
  // viewport if neither side has full room (e.g. a narrow viewport).
  const computeFlyoutPosition = (rowRect, panelRect) => {
    const gap = 4;
    const margin = 8;
    const flyoutWidth = panelRect.width;
    const spaceRight = window.innerWidth - panelRect.right;
    const spaceLeft = panelRect.left;

    let left;
    if (spaceRight >= flyoutWidth + gap) {
      left = panelRect.right + gap;
    } else if (spaceLeft >= flyoutWidth + gap) {
      left = panelRect.left - flyoutWidth - gap;
    } else {
      // Neither side fully fits - open on whichever side has more room and
      // clamp into the viewport instead of letting it run off-screen.
      left =
        spaceRight >= spaceLeft
          ? panelRect.right + gap
          : panelRect.left - flyoutWidth - gap;
      left = Math.max(
        margin,
        Math.min(left, window.innerWidth - flyoutWidth - margin),
      );
    }

    return {
      top: rowRect.top,
      left,
      width: flyoutWidth,
      maxHeight: Math.max(
        160,
        Math.min(360, window.innerHeight - rowRect.top - 16),
      ),
    };
  };

  const activateParent = (slug, rowElement) => {
    setActiveParentSlug(slug);
    const rowRect = rowElement?.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    if (!rowRect || !panelRect) return;
    setFlyoutPosition(computeFlyoutPosition(rowRect, panelRect));
  };

  // Once the panel (and its rows) are mounted, position the flyout for
  // whichever parent is active - covers the reopen-with-selected-parent
  // case, where there's no hover event to trigger `activateParent`.
  useLayoutEffect(() => {
    if (!open || !position || !activeParentSlug) return;
    const rowRect = rowRefs.current[activeParentSlug]?.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    if (!rowRect || !panelRect) return;
    setFlyoutPosition(computeFlyoutPosition(rowRect, panelRect));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, position, activeParentSlug]);

  const normalizedQuery = query.trim().toLowerCase();
  const isSearching = Boolean(normalizedQuery);
  const filteredOptions = !isSearching
    ? options
    : options
        .map((option) => {
          const parentMatches = option.label
            .toLowerCase()
            .includes(normalizedQuery);
          if (parentMatches) return option;
          const matchingSubs = (option.subOptions || []).filter((sub) =>
            sub.label.toLowerCase().includes(normalizedQuery),
          );
          if (matchingSubs.length === 0) return null;
          return { ...option, subOptions: matchingSubs };
        })
        .filter(Boolean);

  return (
    <div className='tw-w-full'>
      <div
        ref={triggerRef}
        role='button'
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        className='tw-flex tw-items-start tw-justify-between tw-gap-2 tw-w-full tw-bg-white tw-border tw-border-solid tw-border-[#d2d6db] tw-rounded-[8px] tw-px-[10px] tw-py-[8px] tw-cursor-pointer'
      >
        {hasSelection ? (
          <div className='tw-flex tw-flex-col tw-min-w-0 tw-flex-1'>
            <span className='tw-text-[11px] tw-leading-[14px] tw-text-[#6c737f] tw-whitespace-normal tw-break-words'>
              {selectedParent.label}
            </span>
            {selectedSub && (
              <span className='tw-text-[14px] tw-leading-[20px] tw-text-[#111927] tw-whitespace-normal tw-break-words'>
                {selectedSub.label}
              </span>
            )}
          </div>
        ) : (
          <span className='tw-min-w-0 tw-flex-1 tw-text-[14px] tw-leading-[20px] tw-text-[#6c737f] tw-whitespace-normal tw-break-words'>
            {placeholder}
          </span>
        )}

        <div className='tw-flex tw-items-center tw-gap-1 tw-shrink-0 tw-mt-[1px]'>
          {hasSelection && (
            <button
              type='button'
              onClick={handleClear}
              aria-label='Clear complaint type'
              className='tw-flex tw-items-center tw-justify-center tw-p-[2px] tw-border-none tw-bg-transparent tw-cursor-pointer tw-text-[#6c737f] hover:tw-text-[#111927]'
            >
              <X size={16} />
            </button>
          )}
          <ChevronDown
            size={18}
            className={`tw-text-[#6c737f] tw-transition-transform tw-duration-200 ${
              open ? 'tw-rotate-180' : ''
            }`}
          />
        </div>
      </div>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: 9999,
            }}
            className='tw-bg-white tw-border tw-border-solid tw-border-[#e5e7eb] tw-rounded-[12px] tw-shadow-[0_4px_16px_rgba(0,0,0,0.12)]'
          >
            <div
              style={{ flexShrink: 0 }}
              className='tw-flex tw-items-center tw-gap-2 tw-px-[10px] tw-py-[8px] tw-border-0 tw-border-b tw-border-solid tw-border-[#e5e7eb]'
            >
              <Search size={16} className='tw-text-[#6c737f] tw-shrink-0' />
              <input
                ref={searchInputRef}
                type='text'
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder='Search complaint type'
                className='tw-flex-1 tw-min-w-0 tw-border-none tw-outline-none tw-text-[14px] tw-leading-[20px] tw-text-[#111927] tw-bg-transparent'
              />
            </div>

            <div
              style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
              className='tw-py-[6px]'
            >
              {filteredOptions.length === 0 ? (
                <p className='tw-text-[13px] tw-leading-[16px] tw-text-[#6c737f] tw-m-0 tw-px-[12px] tw-py-[10px]'>
                  No matching complaint type
                </p>
              ) : (
                filteredOptions.map((option) => {
                  const hasSubOptions = option.subOptions?.length > 0;
                  const isActive = activeParentSlug === option.value;
                  const isSelectedParent = option.value === selectedParentSlug;
                  return (
                    <div
                      key={option.value}
                      className='tw-border-0 tw-border-b tw-border-solid tw-border-[#f3f4f6] last:tw-border-b-0'
                    >
                      <div
                        ref={(node) => {
                          rowRefs.current[option.value] = node;
                        }}
                        role='button'
                        tabIndex={0}
                        onMouseEnter={(event) =>
                          hasSubOptions &&
                          activateParent(option.value, event.currentTarget)
                        }
                        onClick={(event) =>
                          hasSubOptions
                            ? activateParent(option.value, event.currentTarget)
                            : handlePick(option, null)
                        }
                        className={`tw-flex tw-items-start tw-gap-1 tw-justify-between tw-px-[12px] tw-py-[8px] tw-cursor-pointer ${
                          isActive || isSelectedParent
                            ? 'tw-bg-[#f9fafb]'
                            : 'hover:tw-bg-[#f9fafb]'
                        }`}
                      >
                        <p
                          className={`tw-min-w-0 tw-flex-1 tw-text-[12px] tw-leading-[16px] tw-font-medium tw-m-0 tw-whitespace-normal tw-break-words ${
                            isSelectedParent && !hasSubOptions
                              ? 'tw-text-base-green'
                              : 'tw-text-grey-800'
                          }`}
                        >
                          {option.label}
                        </p>
                        {hasSubOptions ? (
                          <ChevronRight
                            size={14}
                            className='tw-text-[#9da4ae] tw-shrink-0 tw-mt-[1px]'
                          />
                        ) : (
                          isSelectedParent && (
                            <Check
                              size={16}
                              className='tw-shrink-0 tw-text-base-green tw-mt-[1px]'
                            />
                          )
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}

      {open &&
        flyoutPosition &&
        (() => {
          const activeOption = filteredOptions.find(
            (option) => option.value === activeParentSlug,
          );
          if (!activeOption?.subOptions?.length) return null;
          return createPortal(
            <div
              ref={flyoutRef}
              style={{
                position: 'fixed',
                top: flyoutPosition.top,
                left: flyoutPosition.left,
                width: flyoutPosition.width,
                maxHeight: flyoutPosition.maxHeight,
                overflowY: 'auto',
                zIndex: 9999,
              }}
              className='tw-bg-white tw-border tw-border-solid tw-border-[#e5e7eb] tw-rounded-[12px] tw-shadow-[0_4px_16px_rgba(0,0,0,0.12)] tw-py-[6px]'
            >
              {activeOption.subOptions.map((sub) => {
                const isSelected =
                  activeOption.value === selectedParentSlug &&
                  sub.value === selectedSubSlug;
                return (
                  <div
                    key={sub.value}
                    role='button'
                    tabIndex={0}
                    onClick={() => handlePick(activeOption, sub)}
                    className={`tw-flex tw-items-start tw-justify-between tw-font-medium tw-gap-2 tw-px-[12px] tw-py-[8px] tw-cursor-pointer tw-text-[14px] tw-leading-[20px] tw-border-0 tw-border-b tw-border-solid tw-border-[#f3f4f6] last:tw-border-b-0 ${
                      isSelected
                        ? 'tw-bg-[#ecfdf3] tw-text-base-green tw-font-medium'
                        : 'tw-text-[#111927] hover:tw-bg-[#f9fafb]'
                    }`}
                  >
                    <span className='tw-min-w-0 tw-flex-1 tw-whitespace-normal tw-break-words'>
                      {sub.label}
                    </span>
                    {isSelected && (
                      <Check size={16} className='tw-shrink-0 tw-mt-[1px]' />
                    )}
                  </div>
                );
              })}
            </div>,
            document.body,
          );
        })()}
    </div>
  );
};

export default ComplaintTypeDropdown;
